import React, { useState, useRef, useEffect, useCallback } from "react";

const ARROW_SIZE = 8;
const GAP = 6;
const VIEWPORT_PADDING = 8;

const Tooltip = ({
  children,
  content,
  placement = "bottom",
  className = "",
  tooltipClassName = "",
  maxWidth = 280,
}) => {
  const [visible, setVisible] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0, arrowOffset: 0 });

  const triggerRef = useRef(null);
  const tooltipRef = useRef(null);

  const updatePosition = useCallback(() => {
    if (!triggerRef.current || !tooltipRef.current) return;

    const triggerRect = triggerRef.current.getBoundingClientRect();
    const tooltipRect = tooltipRef.current.getBoundingClientRect();

    const triggerCenterX = triggerRect.left + triggerRect.width / 2;
    const triggerCenterY = triggerRect.top + triggerRect.height / 2;

    let top = 0;
    let left = 0;

    switch (placement) {
      case "top":
        top = triggerRect.top - tooltipRect.height - GAP;
        left = triggerCenterX - tooltipRect.width / 2;
        break;
      case "bottom":
        top = triggerRect.bottom + GAP;
        left = triggerCenterX - tooltipRect.width / 2;
        break;
      case "left":
        top = triggerCenterY - tooltipRect.height / 2;
        left = triggerRect.left - tooltipRect.width - GAP;
        break;
      case "right":
        top = triggerCenterY - tooltipRect.height / 2;
        left = triggerRect.right + GAP;
        break;
      default:
        top = triggerRect.bottom + GAP;
        left = triggerCenterX - tooltipRect.width / 2;
    }

    // Clamp to viewport
    const clampedLeft = Math.max(
      VIEWPORT_PADDING,
      Math.min(left, window.innerWidth - tooltipRect.width - VIEWPORT_PADDING)
    );

    const clampedTop = Math.max(
      VIEWPORT_PADDING,
      Math.min(top, window.innerHeight - tooltipRect.height - VIEWPORT_PADDING)
    );

    // Arrow offset relative to tooltip
    const arrowOffset =
      placement === "top" || placement === "bottom"
        ? triggerCenterX - clampedLeft
        : triggerCenterY - clampedTop;

    setPosition({
      top: clampedTop,
      left: clampedLeft,
      arrowOffset,
    });
  }, [placement]);

  useEffect(() => {
    if (!visible) return;

    updatePosition();

    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);

    return () => {
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [visible, updatePosition]);

  const show = () => setVisible(true);
  const hide = () => setVisible(false);

  const isTop = placement === "top";
  const isBottom = placement === "bottom";
  const isLeft = placement === "left";
  const isRight = placement === "right";

  return (
    <>
      <div
        ref={triggerRef}
        className={`inline-flex ${className}`}
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocus={show}
        onBlur={hide}
      >
        {children}
      </div>

      {visible && content && (
        <div
          ref={tooltipRef}
          role="tooltip"
          className={`fixed z-[9999] px-4 py-3 rounded-xl text-white text-sm leading-relaxed shadow-lg transition-opacity duration-150 ease-out ${tooltipClassName}`}
          style={{
            top: position.top,
            left: position.left,
            maxWidth,
            background: "#282828",
            opacity: visible ? 1 : 0,
          }}
        >
          {content}

          {/* Arrow */}
          <div
            className="absolute w-0 h-0 border-solid"
            style={{
              borderWidth: ARROW_SIZE,
              borderColor: "transparent",
              ...(isBottom && {
                top: -ARROW_SIZE + 1,
                left: position.arrowOffset,
                transform: "translateX(-50%)",
                borderBottomColor: "#333",
                borderTopWidth: 0,
              }),
              ...(isTop && {
                bottom: -ARROW_SIZE + 1,
                left: position.arrowOffset,
                transform: "translateX(-50%)",
                borderTopColor: "#333",
                borderBottomWidth: 0,
              }),
              ...(isLeft && {
                right: -ARROW_SIZE * 2 + 1,
                top: position.arrowOffset,
                transform: "translateY(-50%)",
                borderLeftColor: "#333",
                borderRightWidth: 0,
              }),
              ...(isRight && {
                left: -ARROW_SIZE * 2 + 1,
                top: position.arrowOffset,
                transform: "translateY(-50%)",
                borderRightColor: "#333",
                borderLeftWidth: 0,
              }),
            }}
          />
        </div>
      )}
    </>
  );
};

export default Tooltip;
